import { useLingui } from '@lingui/react';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import {
  screenPermissionAllows,
  type Navigation,
  type ScreenContext,
  type ScreenManifest,
  type ScreenRegistry,
} from '@/shared/screen';

import {
  createFanoutAdapter,
  createInMemoryAdapter,
  createWebMcpAdapter,
  findWebMcp,
  type InMemoryAgentToolAdapter,
} from './adapter';
import { createClientToolBridge, type ClientToolBridge } from './client-tools';
import type { Confirm } from './confirmation';
import { toolFromAction } from './from-manifest';
import { agentLabels, NO_AGENT_LABELS, type AgentLabels } from './labels';
import { metaTools } from './meta-tools';
import { createAgentToolRegistry, type AgentToolRegistry } from './registry';
import { createScreenContextStore, type ScreenContextStore } from './screen-context';

interface AgentValue {
  screens: ScreenRegistry;
  registry: AgentToolRegistry;
  local: InMemoryAgentToolAdapter;
  contextStore: ScreenContextStore;
  /** O manifesto com os `run` montados que cada tela registrou agora (U56). */
  mounted: Map<string, ScreenManifest>;
  nav: Navigation;
  canUse: (permission: ScreenManifest['permission']) => boolean;
  canAccess?: (screen: ScreenManifest) => boolean;
}

const AgentContext = createContext<AgentValue | null>(null);

export function AgentToolsProvider(props: {
  screens: ScreenRegistry;
  nav: Navigation;
  confirm: Confirm | null;
  canUse: (permission: ScreenManifest['permission']) => boolean;
  canAccess?: (screen: ScreenManifest) => boolean;
  children: ReactNode;
}) {
  const { i18n } = useLingui();
  const { screens, nav, confirm, canUse, canAccess } = props;

  const base = useMemo(() => {
    const local = createInMemoryAdapter();
    const webmcp = findWebMcp();
    const adapter = webmcp ? createFanoutAdapter([local, createWebMcpAdapter(webmcp)]) : local;
    const registry = createAgentToolRegistry({
      adapter,
      confirm,
      declinedMessage: 'The user did not confirm. Nothing was changed.',
      onRefused: (tool, reason) => {
        if (import.meta.env.DEV) console.warn('ferramenta recusada', tool.name, reason);
      },
    });
    return {
      registry,
      local,
      contextStore: createScreenContextStore(),
      mounted: new Map<string, ScreenManifest>(),
      nav,
      canUse,
      ...(canAccess ? { canAccess } : {}),
    };
  }, [confirm, nav, canUse, canAccess]);
  const value = useMemo<AgentValue>(() => ({ ...base, screens }), [base, screens]);

  useEffect(() => {
    const visible = (id: string) => {
      const screen = screens.byId(id);
      return !!screen && screenPermissionAllows(screen, canUse) && (canAccess?.(screen) ?? true);
    };
    value.registry.replaceScope(
      'app',
      'meta',
      metaTools({
        registry: screens,
        i18n,
        nav,
        visible,
        canUse,
        currentContext: value.contextStore.get,
        maskedContext: value.contextStore.getMasked,
        activeScreen: value.contextStore.activeScreen,
        mountedScreen: (id) => value.mounted.get(id),
        screenTools: (id) => value.registry.toolsIn('screen', id),
      }),
    );
    return () => value.registry.clearScope('app', 'meta');
  }, [value, screens, i18n, nav, canUse, canAccess]);

  return <AgentContext.Provider value={value}>{props.children}</AgentContext.Provider>;
}

function useAgent(): AgentValue {
  const value = useContext(AgentContext);
  if (!value) throw new Error('AgentToolsProvider ausente');
  return value;
}

/** Registra as ferramentas e o contexto da tela. Contexto é obrigatório (U46, D2-C); `null` é dívida (`NULL_CONTEXT_DEBT`). */
export function useScreenAgent(screen: ScreenManifest, context: ScreenContext | null) {
  const { registry, contextStore, mounted, nav, canUse, canAccess } = useAgent();
  const accessible = screenPermissionAllows(screen, canUse) && (canAccess?.(screen) ?? true);
  const { i18n } = useLingui();

  useEffect(() => {
    if (screen.agent === false || !accessible) return;
    const actions = screen.actions.filter((a) => a.agent !== false && canUse(a.permission));
    const tools = actions.map((a) => toolFromAction(screen, a, { nav, i18n, scope: 'screen' }));
    registry.replaceScope('screen', screen.id, tools);
    const own: ScreenManifest = { ...screen, actions };
    mounted.set(screen.id, own);
    return () => {
      registry.clearScope('screen', screen.id);
      if (mounted.get(screen.id) === own) mounted.delete(screen.id);
    };
  }, [screen, registry, mounted, nav, i18n, canUse, accessible]);

  useEffect(() => {
    contextStore.set(screen.id, screen.agent === false || !accessible ? null : context);
    return () => contextStore.set(screen.id, null);
  }, [screen, context, contextStore, accessible]);

  const screenId = screen.id;
  const hidden = screen.agent === false || !accessible;
  useEffect(() => {
    if (hidden) return;
    contextStore.enter(screenId);
    return () => contextStore.leave(screenId);
  }, [screenId, hidden, contextStore]);
}

/** Os nomes amigáveis das ferramentas de tela para o chat do Luy (U56); fora do provider, nada é achado. */
export function useAgentLabels(): AgentLabels {
  const value = useContext(AgentContext);
  const { i18n } = useLingui();
  return useMemo(() => (value ? agentLabels({ screens: value.screens, mounted: value.mounted, i18n }) : NO_AGENT_LABELS), [value, i18n]);
}

export function useLocalAgentTools(): InMemoryAgentToolAdapter {
  return useAgent().local;
}

/** A ponte do registro para as `clientTools` do motor (L3), com a `origin` da trilha (U24). */
export function useClientToolBridge(origin: string): ClientToolBridge {
  const { local, screens, contextStore } = useAgent();
  return useMemo(
    () => createClientToolBridge({ tools: local, screens, origin, activeScreen: contextStore.activeScreen }),
    [local, screens, origin, contextStore],
  );
}
